import { useMemo } from "react";
import { every } from "lodash";
import HorizontalBarChart from "@charty/horizontal-bar-chart";
import { isColor } from "@glossy";
import { Metric } from "@linkfluence/models/metric";
import { roundNDecimal } from "@tootools/number";
import { useCurrentUserLocale } from "@radarly/core/user/use-cases";
import { ComparisonPeriod } from "@radarly/models/cards/period";
import { DatavizHorizontalBarChart } from "@radarly/models/dataviz";
import { SearchFragmentTerm } from "@radarly/models/search";

type HorizontalBarChartProps = React.ComponentProps<typeof HorizontalBarChart>;
type MainUnit = "percentage" | "total" | "rating";

const HORIZONTAL_BARCHART_DEFAULT_LIMIT = 5;
const HORIZONTAL_BARCHART_DEFAULT_SHOW_AVATAR = false;

function getMainUnit(
  dataviz: DatavizHorizontalBarChart,
  metric: Metric
): HorizontalBarChartProps["mainUnit"] {
  return metric === Metric.Rating
    ? "rating"
    : dataviz.mainUnit === "average"
    ? "total"
    : dataviz.mainUnit || "total";
}

function getSecondaryUnit(
  dataviz: DatavizHorizontalBarChart,
  mainUnit: MainUnit,
  comparisonPeriod: ComparisonPeriod
) {
  if (mainUnit === "percentage" && comparisonPeriod) {
    return "evolution";
  }
  return dataviz.secondaryUnit === "average" ? "total" : dataviz.secondaryUnit;
}

function getSort(
  dataviz: DatavizHorizontalBarChart,
  metric: Metric,
  sort: HorizontalBarChartProps["sort"]
): HorizontalBarChartProps["sort"] {
  if (metric === Metric.Rating) {
    return "mainValue";
  }
  return (
    sort ||
    (dataviz.sort === "mainUnit" ? "mainValue" : dataviz.sort || "mainValue")
  );
}

export function getPenetrationRateTotal(
  data: SearchFragmentTerm["data"],
  metric: Metric
) {
  return data && data.aggs.metrics ? data.aggs.metrics.metrics[metric] : null;
}

function getOrder(
  dataviz: DatavizHorizontalBarChart,
  metric: Metric,
  order: HorizontalBarChartProps["order"]
) {
  if (metric !== Metric.Rating) {
    return order || "desc";
  }
  return order || dataviz.order || "desc";
}

export function useHorizontalBarChart(
  dataviz: DatavizHorizontalBarChart,
  metric: Metric,
  gauges: HorizontalBarChartProps["gauges"],
  sort: HorizontalBarChartProps["sort"],
  order: HorizontalBarChartProps["order"],
  comparisonPeriod?: ComparisonPeriod
) {
  const locale = useCurrentUserLocale();
  const isEmpty = useMemo(() => every(gauges, { count: 0 }), [gauges]);
  const mainUnit = getMainUnit(dataviz, metric);
  const secondaryUnit = getSecondaryUnit(dataviz, mainUnit, comparisonPeriod);
  return {
    limit: HORIZONTAL_BARCHART_DEFAULT_LIMIT,
    showAvatar: HORIZONTAL_BARCHART_DEFAULT_SHOW_AVATAR,
    displayNullValues: dataviz.displayNullValues,
    locale,
    isEmpty,
    isNarrow: dataviz.isNarrow,
    customColor: isColor(dataviz.color) ? dataviz.color : null,
    mainUnit,
    secondaryUnit,
    order: getOrder(dataviz, metric, order),
    sort: getSort(dataviz, metric, sort),
  };
}

export const getTooltipCount = (mainUnit: MainUnit, count: number): string => {
  if (mainUnit === "rating") {
    const countToScaleFive = count / 20;
    const roundedRating = roundNDecimal(countToScaleFive, 1);
    return roundedRating + " ★";
  }
  return null;
};
